import SingleLineStatistic from "@ihs-widget/components/StatsConsole/Widgets/StatsWidget/parts/SingleLineStatistic";
import useTimeoutVisability from "@ihs-widget/utils/hooks/useTimeoutVisability";
import { useMemo } from "react";

interface DoubleLineStatisticProps {
    label_1: string;
    home_1: number;
    away_1: number;
    isPercentage_1: boolean;
    label_2: string;
    home_2: number;
    away_2: number;
    isPercentage_2: boolean;
    mt?: string;
    mb?: string;
}

const DoubleLineStatistic = ({ label_1, home_1, away_1, isPercentage_1, label_2, home_2, away_2, isPercentage_2, mt = 'mt-[24px]', mb = 'mb-0' }: DoubleLineStatisticProps) => {
    // Check if pair values are null
    const firstPairNull = home_1 === null && away_1 === null;
    const secondPairNull = home_2 === null && away_2 === null;
    const allValuesNull = firstPairNull && secondPairNull;

    // Timeout logic for first pair
    const { isVisible: isFirstVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [firstPairNull, home_1, away_1]
    });

    // Timeout logic for second pair
    const { isVisible: isSecondVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [secondPairNull, home_2, away_2]
    });

    // Timeout logic for all values
    const { isVisible: isComponentVisible } = useTimeoutVisability({
        timeout: 5000,
        dependencies: [allValuesNull, home_1, away_1, home_2, away_2]
    });

    // Hide all component if all values are null for 10 seconds
    if (allValuesNull && !isComponentVisible) {
        return null;
    }

    // If only first pair is null for 10 seconds, show only second pair
    if (firstPairNull && !isFirstVisible && !secondPairNull) {
        return (
            <div className={`w-full h-[28px] flex items-center justify-between flex-row ${mt} ${mb ? mb : 'mb-0'}`}>
                <div className="w-full">
                    <SingleLineStatistic 
                        label={label_2} 
                        home={home_2} 
                        away={away_2} 
                        isPercentage={isPercentage_2} 
                        isSmall={true}
                        data-testid={`stats-console-${label_2.toLowerCase().replace(' ', '-')}`}
                    />
                </div>
            </div>
        );
    }

    // If only second pair is null for 10 seconds, show only first pair
    if (secondPairNull && !isSecondVisible && !firstPairNull) {
        return (
            <div className={`w-full h-[28px] flex items-center justify-between flex-row ${mt} ${mb ? mb : 'mb-0'}`}>
                <div className="w-full">
                    <SingleLineStatistic 
                        label={label_1} 
                        home={home_1} 
                        away={away_1} 
                        isPercentage={isPercentage_1} 
                        isSmall={true}
                        data-testid={`stats-console-${label_1.toLowerCase().replace(' ', '-')}`}
                    />
                </div>
            </div>
        );
    }

    // Standard view with two elements
    return (
        <div className={`w-full h-[28px] flex items-center justify-between flex-row ${mt} gap-[33px] ${mb ? mb : 'mb-0'}`}>
            <div className=""
                style={{
                    width: 'calc(50% - 33px/2)',
                }}
            >
                <SingleLineStatistic 
                    label={label_1} 
                    home={home_1} 
                    away={away_1} 
                    isPercentage={isPercentage_1} 
                    isSmall={true}
                    data-testid={`stats-console-${label_1.toLowerCase().replace(' ', '-')}`}
                />
            </div>
            <div className=""
                style={{
                    width: 'calc(50% - 33px/2)',
                }}
            >
                <SingleLineStatistic 
                    label={label_2} 
                    home={home_2} 
                    away={away_2} 
                    isPercentage={isPercentage_2} 
                    isSmall={true}
                    data-testid={`stats-console-${label_2.toLowerCase().replace(' ', '-')}`}
                />
            </div>
        </div>
    )
}

export default DoubleLineStatistic;